import { track } from "jiffy-analytics";
import { useExperiments } from "@design-lab/shared/lib/config";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

import { useExitIntent } from "@design-lab/shared/hooks/useExitIntent";
import { shouldShowExitIntentModal } from "@design-lab/shared/lib/experiments";
import { SignInModalUtils } from "components/jiffy/sign-in-modal/sign-in-modal-utils";
import {
  MODAL_TRIGGER_POINTS,
  USER_RETENTION_MODAL_SHOWN_KEY,
  type ModalTriggerPoint
} from "../constants";
import { isRecaptureTaskPendingAtom } from "./atoms";

type UseUserRetentionOptions = {
  enabled?: boolean;
  exitIntentEnabled?: boolean;
  hasArtwork?: boolean;
  isSomeArtworkLoading?: boolean;
  hasRequestedDesignServices?: boolean;
  onRecaptured?: () => void;
};

function hasModalBeenShown(): boolean {
  try {
    return sessionStorage.getItem(USER_RETENTION_MODAL_SHOWN_KEY) === "true";
  } catch {
    return false;
  }
}

function markModalAsShown(): void {
  try {
    sessionStorage.setItem(USER_RETENTION_MODAL_SHOWN_KEY, "true");
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

export function useUserRetention(options: UseUserRetentionOptions = {}) {
  const experiments = useExperiments();
  const {
    enabled = true,
    exitIntentEnabled = true,
    hasArtwork = false,
    isSomeArtworkLoading = false,
    hasRequestedDesignServices = false,
    onRecaptured
  } = options;

  const effectiveEnabled = enabled && shouldShowExitIntentModal(experiments?.abExitModalDesignLab);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [triggerPoint, setTriggerPoint] = useState<ModalTriggerPoint>(MODAL_TRIGGER_POINTS.NONE);
  const setRecaptureTaskPending = useSetAtom(isRecaptureTaskPendingAtom);

  const triggerPointRef = useRef<ModalTriggerPoint>(triggerPoint);
  const onRecapturedRef = useRef(onRecaptured);
  const hasArtworkRef = useRef(hasArtwork);
  const hasRequestedDesignServicesRef = useRef(hasRequestedDesignServices);
  const prevHasArtworkRef = useRef(hasArtwork);
  const prevIsSomeArtworkLoadingRef = useRef(isSomeArtworkLoading);
  const prevHasRequestedDesignServicesRef = useRef(hasRequestedDesignServices);

  useEffect(() => {
    triggerPointRef.current = triggerPoint;
  }, [triggerPoint]);

  useEffect(() => {
    onRecapturedRef.current = onRecaptured;
  }, [onRecaptured]);

  useEffect(() => {
    hasArtworkRef.current = hasArtwork;
  }, [hasArtwork]);

  useEffect(() => {
    hasRequestedDesignServicesRef.current = hasRequestedDesignServices;
  }, [hasRequestedDesignServices]);

  const openModal = useCallback(
    (trigger: ModalTriggerPoint) => {
      if (
        !effectiveEnabled ||
        !hasArtworkRef.current ||
        hasModalBeenShown() ||
        hasRequestedDesignServicesRef.current
      )
        return;
      SignInModalUtils.closeModal();
      setTriggerPoint(trigger);
      setIsModalOpen(true);
      markModalAsShown();
    },
    [effectiveEnabled]
  );

  const closeModal = useCallback(() => {
    const currentTrigger = triggerPointRef.current;
    setIsModalOpen(false);
    setTriggerPoint(MODAL_TRIGGER_POINTS.NONE);

    track("retention_modal_ignored", { reason: currentTrigger, source: "design-lab" });
  }, []);

  const handleRecaptured = useCallback(() => {
    const currentTrigger = triggerPointRef.current;
    setIsModalOpen(false);
    setRecaptureTaskPending(true);
    onRecapturedRef.current?.();
    setTriggerPoint(MODAL_TRIGGER_POINTS.NONE);

    track("retention_modal_recaptured", { reason: currentTrigger, source: "design-lab" });
  }, [setRecaptureTaskPending]);

  const shouldMonitor =
    effectiveEnabled &&
    hasArtwork &&
    !isSomeArtworkLoading &&
    !hasModalBeenShown() &&
    !hasRequestedDesignServices;

  const { reset: resetExitIntent } = useExitIntent({
    onExitIntent: () => openModal(MODAL_TRIGGER_POINTS.EXIT_INTENT),
    enabled: shouldMonitor && exitIntentEnabled
  });

  // Reset the exit-intent latch when artwork state transitions back into a
  // monitorable state — otherwise the one-shot ref stays tripped from a prior
  // session within the same page load and the modal never fires again.
  useEffect(() => {
    const wasWithoutArtwork = !prevHasArtworkRef.current;
    const wasLoading = prevIsSomeArtworkLoadingRef.current;
    const wasDesignServicesRequested = prevHasRequestedDesignServicesRef.current;

    prevHasArtworkRef.current = hasArtwork;
    prevIsSomeArtworkLoadingRef.current = isSomeArtworkLoading;
    prevHasRequestedDesignServicesRef.current = hasRequestedDesignServices;

    if (hasModalBeenShown() || hasRequestedDesignServices) return;

    const artworkJustFinished = wasLoading && !isSomeArtworkLoading && hasArtwork;
    const firstArtworkReady = wasWithoutArtwork && hasArtwork && !isSomeArtworkLoading;
    const designServicesJustCancelled =
      wasDesignServicesRequested &&
      !hasRequestedDesignServices &&
      hasArtwork &&
      !isSomeArtworkLoading;

    if (artworkJustFinished || firstArtworkReady || designServicesJustCancelled) {
      resetExitIntent();
    }
  }, [hasArtwork, isSomeArtworkLoading, hasRequestedDesignServices, resetExitIntent]);

  useEffect(() => {
    if (triggerPoint !== MODAL_TRIGGER_POINTS.NONE) {
      track("retention_modal_load", { reason: triggerPoint, source: "design-lab" });
    }
  }, [triggerPoint]);

  return {
    isModalOpen,
    triggerPoint,
    openModal,
    closeModal,
    handleRecaptured,
    resetExitIntent
  };
}
